import { URLS } from "./constant";

/**
 * Brand logo per NetSuite brand id, for the 36 partner brands whose logos the business
 * hosts (`URLS.BRANDS` — the same single source `ShopByBrand` renders).
 * Everything here is a **reference into that map**, never a second copy of a URL.
 *
 * **Keyed by the customlist brand id** (`CUSTOMLIST_DND_WEB_BRANDS`), the same rule as
 * `constants/categoryIcons.js`: an id survives a rename, and the brand surfaces
 * (`BrandsDropdown`, the drawer) are built from `{ brand_id, brand_name }` rows, so the
 * id is what's in hand at render time.
 *
 * The pairing was **matched against the live brand list, not guessed**: slugs and names
 * were normalised (`&` → "and", strip non-alphanumerics) and every entry below is either
 * an exact or a unique-prefix match — 35 of 36 resolved automatically with zero
 * ambiguity. The one hand-match is `morita` → "J. Morita" (id 157), whose leading
 * initial defeats the prefix rule. 313 − 36 brands have no hosted logo; consumers fall
 * back to the letter tile (`brandInitial` in `BrandsDropdown`).
 */
export const BRAND_LOGO_BY_ID = {
  30: URLS.BRANDS["aurelia"], // Aurelia
  87: URLS.BRANDS["d2-healthcare"], // D2
  91: URLS.BRANDS["defend"], // Defend
  102: URLS.BRANDS["dentamerica"], // Dentamerica
  107: URLS.BRANDS["dentsply"], // Dentsply
  112: URLS.BRANDS["diadent"], // DiaDent
  116: URLS.BRANDS["dmg"], // DMG
  127: URLS.BRANDS["flight"], // Flight Dental
  128: URLS.BRANDS["flow"], // Flow X-Ray
  137: URLS.BRANDS["hager"], // Hager
  138: URLS.BRANDS["halyard"], // Halyard
  156: URLS.BRANDS["johnson-and-johnson"], // Johnson & Johnson
  157: URLS.BRANDS["morita"], // J. Morita — hand-matched; initial defeats the prefix rule
  162: URLS.BRANDS["kerr"], // Kerr
  163: URLS.BRANDS["keystone"], // Keystone
  167: URLS.BRANDS["kuraray"], // Kuraray
  171: URLS.BRANDS["mark3"], // Mark 3
  177: URLS.BRANDS["medicom"], // Medicom
  184: URLS.BRANDS["microcopy"], // Microcopy
  210: URLS.BRANDS["pacdent"], // Pacdent
  212: URLS.BRANDS["palmero"], // Palmero
  215: URLS.BRANDS["parkell"], // Parkell
  218: URLS.BRANDS["pdi"], // PDI
  228: URLS.BRANDS["premier"], // Premier
  231: URLS.BRANDS["pulpdent"], // Pulpdent
  242: URLS.BRANDS["sable"], // Sable
  250: URLS.BRANDS["shofu"], // Shofu
  276: URLS.BRANDS["tidi"], // Tidi
  293: URLS.BRANDS["waterpik"], // Waterpik
  297: URLS.BRANDS["young"], // Young
  300: URLS.BRANDS["zirc"], // Zirc
  302: URLS.BRANDS["surgical-specialties"], // Surgical Specialties
  325: URLS.BRANDS["pierrel"], // Pierrel
  333: URLS.BRANDS["univet"], // Univet
  344: URLS.BRANDS["primed"], // Primed
  360: URLS.BRANDS["3m"], // 3M Health Care (now Solventum)
};

// Normalise on the way out: the source map's URLs are hand-typed — some carry stray
// padding, and 11 of 36 contain **raw spaces** (`/extra images/…`). Browsers quietly
// encode those when fetching, but anything stricter (curl, some proxies) rejects them.
// Only raw spaces are replaced; already-encoded `%20`s must not be touched, which rules
// out `encodeURI` (it would double-encode them to `%2520`).
export const brandLogo = (id) => {
  const raw = BRAND_LOGO_BY_ID[id];
  return raw ? raw.trim().replace(/ /g, "%20") : null;
};
